import React, { useState } from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { Heading } from '@fiverr-private/typography';
import { I18n } from '@fiverr-private/i18n-react';
import {
  Lightbox,
  LightboxHeader,
  LightboxLabel,
  LightboxContent,
  LightboxArrows,
  LightboxSource,
} from '@fiverr-private/overlay';
import type { Gql } from '@fiverr-private/dynamic_matching';
import LightboxSourceItem from './LightboxSourceItem';
import { extractOriginalMediaFromAttachment, transformGqlMediaTypeToLightboxSourceType } from './utils';

const transformToLightboxSource = (attachment: Gql.BriefAttachment): LightboxSource => {
  const mediaType = transformGqlMediaTypeToLightboxSourceType(attachment.type);

  return {
    src: extractOriginalMediaFromAttachment(attachment),
    alt: `brief-lightbox-attachment-${attachment.id}`,
    type: mediaType,
  };
};

interface AttachmentsGalleryProps {
  attachments: Gql.DmBrief['attachments'];
}

export const AttachmentsGallery: React.FC<AttachmentsGalleryProps> = ({ attachments }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [sourceInitialIndex, setSourceInitialIndex] = useState<number>(0);

  const handleSelectSource = (index: number) => {
    setSourceInitialIndex(index);
    setIsOpen(true);
  };

  if (!attachments?.length) {
    return null;
  }

  // Transform attachments to LightboxSource
  const lightboxSources = attachments.map(transformToLightboxSource);

  return (
    <Stack direction="column" gap="1">
      <Heading size="h_xxs">
        <I18n k="brief_preview_drawer.person_brief.attachments" />
      </Heading>
      <Stack direction="column">
        <Stack direction="row" overflowX="scroll" gap="4">
          {attachments?.map((attachment, index) => (
            <LightboxSourceItem key={attachment.id} attachment={attachment} onClick={() => handleSelectSource(index)} />
          ))}
        </Stack>
        {isOpen && (
          <Lightbox sources={lightboxSources} onClose={() => setIsOpen(false)} sourceInitialIndex={sourceInitialIndex}>
            <LightboxHeader />
            <LightboxArrows />
            <LightboxContent />
            <LightboxLabel />
          </Lightbox>
        )}
      </Stack>
    </Stack>
  );
};
